import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../utils/agGridSetup';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community';
import { Search, Plus, MoreVertical, Trash2, Pencil, X, Download, ArrowLeft, Upload, Copy, Printer, Share2, ArrowRight } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import CreateShareLinkModal from './modals/CreateShareLinkModal';
import EditShareLinkModal from './modals/EditShareLinkModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import StatusMessage, { StatusType } from '../../components/StatusMessage';
import { useShareLinks } from '../../hooks/useShareLinks';
import { ShareLinkDto } from '../../services/shareLinkApi';
import { ExportDataTypes } from '../../utils/helperFunctions';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../styles/pages/alerts.css';
import '../../styles/pages/share-link-additional.css';
import '../../styles/pages/DeviceTableView.css';
import '../../styles/pages/DeviceDetailPage.css';
import '../../styles/pages/livedata.css';
import '../../styles/zoom.css';
import { options } from '../../utils/helperFunctions';
import { getUserTimezone } from '../../utils/timezoneUtils';
import { downloadBlob, printBlob, shareContent } from '../../utils/downloadFile';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import { DevicesContext } from '../../contexts/DevicesContext';

const ShareLinkPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    shareLinks,
    loading,
    error,
    createShareLink,
    updateShareLink,
    deleteShareLink,
    fetchShareLinks,
    clearError,
  } = useShareLinks();

  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedShareLink, setSelectedShareLink] = useState<ShareLinkDto | null>(null);
  const [shareLinkToDelete, setShareLinkToDelete] = useState<ShareLinkDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState<number | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{top: number, left: number} | null>(null);
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  const timeZone = userPreferences.timezone || getUserTimezone();
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showMobileExportModal, setShowMobileExportModal] = useState(false);
  const [qrModalUrl, setQrModalUrl] = useState<string | null>(null);
  const qrCanvasWrapperRef = useRef<HTMLDivElement>(null);
  const [expandedDeviceLinkId, setExpandedDeviceLinkId] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{top: number, left: number} | null>(null);
  // Status message state
  const [statusMessage, setStatusMessage] = useState<{
    type: StatusType;
    message: string;
    isVisible: boolean;
  }>({
    type: 'success',
    message: '',
    isVisible: false,
  });

  // Grid reference for API calls
  const gridRef = useRef<AgGridReact>(null);

  /* User Selected Context */
  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }
  const { hideBottomBar, showBottomBar, isBottomBarHidden } = userSelectedContext;

  /* Devices Context — used to block creation until a device is onboarded */
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const {
    devicesForDashboard: devices,
    getDevicesForDashboard,
    demoDevice,
  } = devicesContext;

  useEffect(() => {
    if (devices.length === 0) getDevicesForDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle error from useShareLinks hook
  useEffect(() => {
    if (error) {
      setStatusMessage({
        type: 'error',
        message: error,
        isVisible: true,
      });
      clearError();
    }
  }, [error, clearError]);

  // Handle screen size changes
  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Un hide bottom bar when modal is closed
    useEffect(() => {
      if (screenSize <= 768 && isBottomBarHidden && !(isCreateModalOpen || isEditModalOpen)) {
        showBottomBar();
      }
    }, [isCreateModalOpen, isEditModalOpen]);

  // Close device tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.share-link-mobile-device-more') && 
          !target.closest('.share-link-mobile-device-tooltip')) {
        setExpandedDeviceLinkId(null);
        setTooltipPosition(null);
      }
    };

    if (expandedDeviceLinkId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [expandedDeviceLinkId]);

  // Auto-close tooltip after 10 seconds
  useEffect(() => {
    if (expandedDeviceLinkId !== null) {
      const timer = setTimeout(() => {
        setExpandedDeviceLinkId(null);
        setTooltipPosition(null);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [expandedDeviceLinkId]);

  // Demo-link hiding is disabled: backend deletes demo share-links once the
  // user onboards a real device, so the client no longer filters them. If that
  // behavior is reverted, uncomment the hideDemo block below.
  // const hideDemo = !isDemoAccountAllowed();
  const filteredShareLinks = shareLinks.filter((link: ShareLinkDto) => {
    // if (hideDemo && !Number.isNaN(demoDeviceId)) {
    //   const details = link.details || [];
    //   if (details.length > 0 && details.every(d => d.deviceId === demoDeviceId)) {
    //     return false;
    //   }
    // }
    return link.shareLinkName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleCreateClick = () => {
    if (devices.length === 0 && !demoDevice) {
      setStatusMessage({
        type: 'warning',
        message: 'Please onboard a device first to create a share link. Visit the Devices page to add your first device.',
        isVisible: true,
      });
      return;
    }
    screenSize <= 768 && hideBottomBar();
    setIsCreateModalOpen(true);
  };

  // Handler for dropdown toggle
  const handleDropdownToggle = (shareLinkId: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.right - 180, // adjust width offset
    });
    
    setOpenDropdownId(openDropdownId === shareLinkId ? null : shareLinkId);
  };

  const handleEditClick = (shareLink: ShareLinkDto) => {
    screenSize <= 768 && hideBottomBar();
    setSelectedShareLink(shareLink);
    setIsEditModalOpen(true);
    setShowActionMenu(null);
    setOpenDropdownId(null);
    setDropdownPosition(null);
  };

  const handleDeleteClick = (shareLink: ShareLinkDto) => {
    setShareLinkToDelete(shareLink);
    setIsDeleteModalOpen(true);
    setShowActionMenu(null);
    setOpenDropdownId(null);
    setDropdownPosition(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (_event: MouseEvent) => {
      if (openDropdownId !== null) {
        setOpenDropdownId(null);
        setDropdownPosition(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdownId]);


  // Generate a random alphanumeric URL code (like 4QM1FBTEREK8RUOO)
  const generateUniqueUrlCode = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const length = 16;
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleCreateShareLink = async (data: { 
    name: string; 
    deviceIds: number[];
    layout?: string;
    parameters?: string[];
  }) => {
    try {
      console.log('handleCreateShareLink called with data:', data);
      
      // Generate a unique random URL code (e.g., 4QM1FBTEREK8RUOO)
      const uniqueUrl = generateUniqueUrlCode();

      // Convert deviceIds and parameters into details array
      const details = data.deviceIds.map(deviceId => ({
        deviceId: deviceId,
        parameterIds: data.parameters?.join(',') || '1,2,3,4,5,6,7,8' // Default all parameters if not specified
      }));

      const requestPayload = {
        shareLinkName: data.name,
        url: uniqueUrl,
        details: details,
      };

      console.log('Creating share link with payload:', requestPayload);
      const result = await createShareLink(requestPayload);
      console.log('Share link created successfully:', result);
      
      if (result) {
        setStatusMessage({
          type: 'success',
          message: 'Share link created successfully!',
          isVisible: true,
        });
        setIsCreateModalOpen(false);
        // Reload grid to get latest values from server
        await fetchShareLinks();
      }
    } catch (error) {
      console.error('Error creating share link:', error);
      setStatusMessage({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to create share link',
        isVisible: true,
      });
    }
  };

  const handleEditShareLink = async (data: { name: string; deviceIds: number[]; parameters?: string[] }) => {
    if (!selectedShareLink) return;
    
    try {
      // Convert deviceIds and parameters into details array
      const details = data.deviceIds.map(deviceId => ({
        deviceId: deviceId,
        parameterIds: data.parameters?.join(',') || '1,2,3,4,5,6,7,8' // Default all parameters if not specified
      }));

      const result = await updateShareLink(selectedShareLink.id, {
        shareLinkName: data.name,
        url: selectedShareLink.url, // Keep the same URL
        details: details,
      });
      if (result) {
        setStatusMessage({
          type: 'success',
          message: 'Share link updated successfully!',
          isVisible: true,
        });
        setIsEditModalOpen(false);
        setSelectedShareLink(null);
      }
    } catch (error) {
      setStatusMessage({
        type: 'error',
        message: 'Failed to update share link',
        isVisible: true,
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!shareLinkToDelete) return;
    
    setIsDeleting(true);
    try {
      const result = await deleteShareLink(shareLinkToDelete.id);
      if (result) {
        setStatusMessage({
          type: 'success',
          message: 'Share link deleted successfully!',
          isVisible: true,
        });
        setIsDeleteModalOpen(false);
        setShareLinkToDelete(null);
      }
    } catch (error) {
      setStatusMessage({
        type: 'error',
        message: 'Failed to delete share link',
        isVisible: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const closeStatusMessage = () => {
    setStatusMessage(prev => ({ ...prev, isVisible: false }));
  };

  // Export handlers
  const handleExportDropdownToggle = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (screenSize <= 768) {
      setShowMobileExportModal(true);
      return;
    }
    
    setShowExportDropdown(!showExportDropdown);
  };

  const handleExportCSV = async () => {
    try {
      // Get all row data (including filtered data)
      let allRowData: ShareLinkDto[] = [];
      
      if (gridRef.current?.api) {
        // Desktop: Get data from grid (respects filters and sorting)
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
          if (node.data) {
            allRowData.push(node.data);
          }
        });
      } else {
        // Mobile: Use filtered share links directly
        allRowData = filteredShareLinks;
      }

      if (allRowData.length === 0) {
        setStatusMessage({
          type: 'warning',
          message: 'No data available to download for the selected date range. Please try a different period.',
          isVisible: true,
        });
        setShowExportDropdown(false);
        setShowMobileExportModal(false);
        return;
      }

      // Prepare CSV data
      const csvData = allRowData.map((link) => ({
        'Share Link Name': link.shareLinkName,
        'Devices': link.details && link.details.length > 0
          ? link.details.map((detail: any) => detail.deviceName || 'Unnamed Device').join(', ')
          : `${link.devices} device${link.devices > 1 ? 's' : ''}`,
        'URL': `${window.location.origin}${process.env.PUBLIC_URL || ''}/${link.url}`,
        'Created On': formatDate(link.createdOn),
      }));

      // Convert to CSV
      const headers = Object.keys(csvData[0]);
      const csvRows = [
        headers.join(','),
        ...csvData.map((row) =>
          headers.map((header) => {
            const value = row[header as keyof typeof row];
            // Escape commas and quotes in CSV
            if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        ),
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      await downloadBlob(
        blob,
        `ShareLinks_${new Date().toISOString().split('T')[0]}.csv`,
        'text/csv;charset=utf-8;',
      );

      setStatusMessage({
        type: 'success',
        message: 'Data exported to CSV successfully!',
        isVisible: true,
      });
      setShowExportDropdown(false);
      setShowMobileExportModal(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setStatusMessage({
        type: 'error',
        message: 'Failed to export data to CSV',
        isVisible: true,
      });
    }
  };

  const handleExportExcel = async () => {
    try {
      // Get all row data (including filtered data)
      let allRowData: ShareLinkDto[] = [];
      
      if (gridRef.current?.api) {
        // Desktop: Get data from grid (respects filters and sorting)
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
          if (node.data) {
            allRowData.push(node.data);
          }
        });
      } else {
        // Mobile: Use filtered share links directly
        allRowData = filteredShareLinks;
      }

      if (allRowData.length === 0) {
        setStatusMessage({
          type: 'warning',
          message: 'No data available to download for the selected date range. Please try a different period.',
          isVisible: true,
        });
        setShowExportDropdown(false);
        setShowMobileExportModal(false);
        return;
      }

      // Prepare Excel data
      const excelData = allRowData.map((link) => ({
        'Share Link Name': link.shareLinkName,
        'Devices': link.details && link.details.length > 0
          ? link.details.map((detail: any) => detail.deviceName || 'Unnamed Device').join(', ')
          : `${link.devices} device${link.devices > 1 ? 's' : ''}`,
        'URL': `${window.location.origin}${process.env.PUBLIC_URL || ''}/${link.url}`,
        'Created On': formatDate(link.createdOn),
      }));

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Share Links');

      // Set column widths
      const columnWidths = [
        { wch: 25 }, // Share Link Name
        { wch: 10 }, // Devices
        { wch: 50 }, // URL
        { wch: 25 }, // Created On
      ];
      worksheet['!cols'] = columnWidths;

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      await downloadBlob(
        blob,
        `ShareLinks_${new Date().toISOString().split('T')[0]}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );

      setStatusMessage({
        type: 'success',
        message: 'Data exported to Excel successfully!',
        isVisible: true,
      });
      setShowExportDropdown(false);
      setShowMobileExportModal(false);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      setStatusMessage({
        type: 'error',
        message: 'Failed to export data to Excel',
        isVisible: true,
      });
    }
  };

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showExportDropdown) {
        const target = event.target as HTMLElement;
        if (!target.closest('.export-dropdown-container')) {
          setShowExportDropdown(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showExportDropdown]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${day} ${month} ${year}, ${hours}:${minutes}:${seconds}`;
  };

  // QR modal helpers
  const getQrCanvas = (): HTMLCanvasElement | null => {
    return qrCanvasWrapperRef.current?.querySelector('canvas') || null;
  };

  const getQrBlob = (): Promise<Blob | null> => {
    const canvas = getQrCanvas();
    if (!canvas) return Promise.resolve(null);
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'));
  };

  const handleQrCopyLink = async () => {
    if (!qrModalUrl) return;
    try {
      await navigator.clipboard.writeText(qrModalUrl);
      setStatusMessage({ type: 'success', message: 'Link copied to clipboard!', isVisible: true });
    } catch (err) {
      setStatusMessage({ type: 'error', message: 'Failed to copy link', isVisible: true });
    }
  };

  const handleQrDownload = async () => {
    const blob = await getQrBlob();
    if (!blob) return;
    await downloadBlob(blob, 'share-link-qr.png', 'image/png');

    setTimeout(() => {
      setStatusMessage({ type: 'success', message: 'QR code downloaded successfully!', isVisible: true });
    }, 1500);
  };

  const handleQrPrint = async () => {
    const canvas = getQrCanvas();
    if (!canvas || !qrModalUrl) return;
    // Inside the RN WebView there is no window.print() — hand the image to native.
    const blob = await getQrBlob();
    if (blob && (await printBlob(blob, 'share-link-qr.png', 'image/png'))) return;
    const dataUrl = canvas.toDataURL('image/png');
    // Use iframe for WebView compatibility (window.open may be blocked)
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) { document.body.removeChild(iframe); return; }
    doc.open();
    doc.write(`
      <html>
        <head><title>Share Link QR Code</title></head>
        <body style="text-align:center; font-family: Arial, sans-serif; padding:24px;">
          <h2>Scan QR Code</h2>
          <img src="${dataUrl}" style="width:280px;height:280px;" />
          <p style="word-break:break-all; margin-top:16px;">${qrModalUrl}</p>
        </body>
      </html>
    `);
    doc.close();
    iframe.onload = () => {
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
  };

  const handleQrOpenInBrowser = () => {
    if (!qrModalUrl) return;
    window.open(qrModalUrl, '_blank', 'noopener,noreferrer');
  };

  const handleQrShare = async () => {
    if (!qrModalUrl) return;
    const blob = await getQrBlob();
    const shared = await shareContent({
      url: qrModalUrl,
      title: 'Share Link',
      text: 'Scan this QR code',
      blob,
      fileName: 'share-link-qr.png',
      mimeType: 'image/png',
    });
    if (!shared) {
      handleQrCopyLink();
      setStatusMessage({ type: 'success', message: 'Sharing not supported — link copied instead', isVisible: true });
    }
  };

  // Column definitions for AG Grid
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'Share Link Name',
      field: 'shareLinkName',
      flex: 1,
      sortable: true,
      filter: false,
      wrapText: true,
    },
    {
      headerName: 'Devices',
      field: 'devices',
      cellRenderer: (params: any): React.ReactNode => {
        const shareLink = params.data;
        if (shareLink.details && shareLink.details.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {shareLink.details.map((detail: any, index: number) => (
                <span key={detail.deviceId || index}>
                  <span
                    className="text-[#005EE5] dark:text-[#A9CCFE] hover:underline cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (detail.deviceId) {
                        navigate(`/devices/${detail.deviceId}`);
                      }
                    }}
                  >
                    {detail.deviceName || 'Unnamed Device'}
                  </span>
                  {index < shareLink.details.length - 1 && <span>, </span>}
                </span>
              ))}
            </div>
          );
        }
        return `${params.value} device${params.value > 1 ? 's' : ''}`;
      },
      flex: 1.5,
      sortable: true,
      filter: false,
      wrapText: true,
    },
    {
      headerName: 'URL',
      field: 'url',
      cellRenderer: (params: any) => {
        const url = params.value;
        const baseUrl = window.location.origin;
        const basename = process.env.PUBLIC_URL || '';
        const fullUrl = `${baseUrl}${basename}/${url}`;

        return (
          <div className="url-container">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setQrModalUrl(fullUrl);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.target.blur()}
              className="share-link-url share-link-url-button"
              title={`Click to view QR: ${fullUrl}`}
            >
              {fullUrl}
            </button>
          </div>
        );
      },
      flex: 2,
      sortable: false,
      filter: false,
      wrapText: true,
    },
    {
      headerName: 'Created On',
      field: 'createdOn',
      cellRenderer: (params: any) => {
        return new Date(params.value).toLocaleString('en-US', {timeZone: timeZone, ...options});
      },
      flex: 1.2,
      sortable: true,
      filter: false,
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (params: any) => {
        const shareLink = params.data;
        const shareLinkId = shareLink.id;
        
        return (
          <div className="relative flex justify-center">
            <button
              onClick={(e) => {
                handleDropdownToggle(shareLinkId, e);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.target.blur()}
              className="p-2 hover:bg-[var(--button-hover)] rounded-full transition-colors focus:outline-none focus:ring-0 active:outline-none"
            >
              <MoreVertical className="w-4 h-4 text-[var(--color-text)]" />
            </button>
          </div>
        );
      },
      minWidth: 100,
      width: 100,
      sortable: false,
      filter: false,
      resizable: false,
      pinned: 'right',
    },
  ], [openDropdownId, handleDropdownToggle]);

  // Grid options
  const gridOptions: GridOptions = useMemo(() => ({
    defaultColDef: {
      resizable: true,
      sortable: true,
      filter: false,
    },
    columnDefs: columnDefs,
    pagination: true,
    paginationPageSize: 10,
    suppressPaginationPanel: true,
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    suppressRowDeselection: true,
    domLayout: 'normal',
    rowHeight: 60,
    headerHeight: 75,
    overlayNoRowsTemplate: '<span class="custom-no-rows">Create your first share link to get started.</span>',
    // Enable sorting for all columns
    sortable: true,
    // Enable multi-column sorting
    multiSortKey: 'ctrl',
    getRowHeight: () => {
      if (screenSize <= 1000) return 120;
    },
    icons: {
      sortAscending: '<span style="font-size:12px;">▲</span>',
      sortDescending: '<span style="font-size:12px;">▼</span>',
    },
  }), [columnDefs, screenSize]);

  // Apply search filter when searchTerm prop changes
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('quickFilterText', searchTerm);
    }
  }, [searchTerm]);

  // Refresh grid when shareLinks data changes
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('rowData', filteredShareLinks);
    }
  }, [filteredShareLinks]);

  const renderMobileCards = () => {
    if (filteredShareLinks.length === 0) {
      return (
        <div className="share-link-mobile-container">
          <div className="mobile-empty-state">
            <div className="mobile-empty-icon">🔗</div>
            <h3 className="mobile-empty-title">No Share Links Found</h3>
            <p className="mobile-empty-description">
              {searchTerm
                ? 'No share links match your current search. Try adjusting your search terms.'
                : `You haven't created any share links yet. Create your first ${devices.length === 1 && demoDevice ? 'Demo ' : ''}share link to get started.`
              }
            </p>
            {!searchTerm && (
              <button
                onClick={handleCreateClick}
                className="mobile-empty-button"
              >
                <Plus className="w-4 h-4" />
                Create First Share Link
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="share-link-mobile-container">
        {filteredShareLinks.map((link: ShareLinkDto) => {
          const fullUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}/${link.url}`;
          return (
            <div key={link.id} className="share-link-mobile-card">
              <div className="share-link-mobile-card-header">
                <h3 className="share-link-mobile-card-title">{link.shareLinkName}</h3>
              </div>
              
              <div className="share-link-mobile-card-content">
                <div className="share-link-mobile-card-body">
                  <div className="share-link-mobile-devices-row">
                    <span className="share-link-mobile-devices">
                      {link.details && link.details.length > 0 ? (
                        <>
                          <span className="share-link-mobile-devices-label">Devices: </span>
                          {link.details.slice(0, 2).map((detail: any, index: number) => (
                            <span key={detail.deviceId || index}>
                              <span
                                className="share-link-mobile-device-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (detail.deviceId) {
                                    navigate(`/devices/${detail.deviceId}`);
                                  }
                                }}
                              >
                                {detail.deviceName || 'Unnamed Device'}
                              </span>
                              {index < link.details.slice(0, 2).length - 1 && <span>, </span>}
                            </span>
                          ))}
                          {link.details.length > 2 && (
                            <>
                              <span>, </span>
                              <span
                                className="share-link-mobile-device-more"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const target = e.currentTarget;
                                  const rect = target.getBoundingClientRect();
                                  if (expandedDeviceLinkId === link.id) {
                                    setExpandedDeviceLinkId(null);
                                    setTooltipPosition(null);
                                  } else {
                                    setExpandedDeviceLinkId(link.id);
                                    setTooltipPosition({
                                      top: rect.bottom + 8,
                                      left: rect.left + rect.width / 2
                                    });
                                  }
                                }}
                              >
                                +{link.details.length - 2}
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        `Devices: ${link.devices}`
                      )}
                    </span>
                  </div>
                  {expandedDeviceLinkId === link.id && tooltipPosition && (
                    <div 
                      className="share-link-mobile-device-tooltip"
                      style={{
                        position: 'fixed',
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      {link.details.slice(2).map((detail: any, index: number) => (
                        <span key={detail.deviceId || index}>
                          <span
                            className="share-link-mobile-device-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (detail.deviceId) {
                                setExpandedDeviceLinkId(null);
                                setTooltipPosition(null);
                                navigate(`/devices/${detail.deviceId}`);
                              }
                            }}
                          >
                            {detail.deviceName || 'Unnamed Device'}
                          </span>
                          {index < link.details.slice(2).length - 1 && <span>, </span>}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="share-link-mobile-url-section">
                    <span className="share-link-mobile-url-label">Url</span>
                    <div className="share-link-mobile-url-container">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setQrModalUrl(fullUrl);
                        }}
                        className="share-link-mobile-url-link share-link-mobile-url-button"
                        title="Click to view QR code"
                      >
                        {fullUrl}
                      </button>
                      <button
                        className="share-link-mobile-menu-button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowActionMenu(showActionMenu === link.id ? null : link.id);
                        }}
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="share-link-mobile-date-row">
                    <span className="share-link-mobile-date">
                      {formatDate(link.createdOn)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMobileActionMenu = () => {
    if (!showActionMenu) return null;
    
    const selectedLink = filteredShareLinks.find((link: ShareLinkDto) => link.id === showActionMenu);
    if (!selectedLink) return null;

    return (
      <>
        <div className="mobile-action-menu-overlay" onClick={() => setShowActionMenu(null)}>
          <div className="mobile-action-menu">
            <button
              className="mobile-action-close"
              onClick={() => setShowActionMenu(null)}
            >
              <X className="w-5 h-5 text-[var(--color-text)]" />
            </button>
            <div className="mobile-action-content">
              <button
                className="mobile-action-item"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditClick(selectedLink);
                  setShowActionMenu(null);
                }}
              >
                <Pencil className="w-5 h-5" />
                Edit Details
              </button>
              <button
                className="mobile-action-item delete"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteClick(selectedLink);
                  setShowActionMenu(null);
                }}
              >
                <Trash2 className="w-5 h-5" />
                Remove Share Link
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderMobileExportModal = () => {
    if (!showMobileExportModal) return null;

    return (
      <>
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowMobileExportModal(false)}
        >
          <div className="mobile-modal">
            <div className="mobile-modal-header">
              <h3 className="mobile-modal-title">Select Export Type</h3>
              <X
                className="w-5 h-5 text-[var(--color-text)]"
                onClick={() => setShowMobileExportModal(false)}
              />
            </div>
            <div className="mobile-modal-content">
              {ExportDataTypes.filter(
                type => type.value === 'excel' || type.value === 'csv'
              ).map(type => (
                <div
                  key={type.value}
                  className="mobile-modal-option"
                  onClick={() => {
                    if (type.value === 'csv') {
                      handleExportCSV();
                    } else if (type.value === 'excel') {
                      handleExportExcel();
                    }
                    setShowMobileExportModal(false);
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/SVGIcons/${type.label}.svg`}
                    alt={type.label}
                  />
                  <span>{type.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <DashboardLayout>
      <div className={`alerts-container share-link-container ${screenSize <= 768 && (isCreateModalOpen || isEditModalOpen) ? 'isMobileModalOpen' : ''}`}>
        {/* Loading Indicator */}
        {loading && (
          <div className="h-[calc(100dvh-5rem)] loading-container">
            <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full"></div>
            <p className="text-base text-[var(--color-sub-title-text)]">
              Loading share links...
            </p>
          </div>
        )}

        {!loading && (
          <>
            {/* Header */}
            <div className="alerts-page-header zoomOutLevel">
              {screenSize <= 768 ? (
                <>
                  {/* Mobile Header */}
                  <div className="share-link-mobile-header">
                    <div className="share-link-mobile-header-left">
                      <ArrowLeft 
                        className="share-link-back-icon" 
                        size={24}
                        onClick={() => navigate(-1)}
                      />
                      <h1 className="share-link-mobile-title">Share Link</h1>
                    </div>
                  </div>
                  
                  {/* Mobile Search Bar */}
                  <div className="share-link-mobile-search-container">
                    <div className="share-link-mobile-search-bar">
                      <input
                        type="text"
                        placeholder="Search by link name"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="share-link-mobile-search-input"
                      />
                      <Search className="share-link-mobile-search-icon" size={20} />
                    </div>
                    <div className="share-link-mobile-action-buttons">
                      <button
                        className="share-link-mobile-icon-button-upload"
                        onClick={handleExportDropdownToggle}
                      >
                        <Upload className="w-5 h-5" />
                      </button>
                      <button
                        className="share-link-mobile-icon-button"
                        onClick={handleCreateClick}
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="alerts-page-header-left">
                    <h1 className="device-title">{screenSize <= 860 ? 'Share Links' : 'Share Link Management'}</h1>
                    <p className="alerts-subtitle device-subtitle ml-0">
                      Create and manage shareable device links
                    </p>
                  </div>

                  {/* Action Bar */}
                  <div className="alerts-page-header-right">
                    {/* Search Bar */}
                    <div className='search-bar-container flex-1 lg:min-w-[400px]'>
                      <input
                        type="text"
                        placeholder="Search by link name"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                      <div className="search-bar-icon">
                        <Search className="w-6 h-6 text-white" />
                      </div>
                    </div>

                    {/* Export Data Button */}
                    <div className="export-dropdown-container">
                      <button
                        className={`action-button ${showExportDropdown ? 'active' : ''}`}
                        onClick={handleExportDropdownToggle}
                      >
                        <Download className="w-5 h-5 text-[var(--color-terms-link)]" />
                      </button>
                      {showExportDropdown && (
                        <div className="export-dropdown">
                          {ExportDataTypes.filter(
                            type => type.value === 'excel' || type.value === 'csv'
                          ).map(type => (
                            <div
                              key={type.value}
                              className="export-dropdown-item"
                              onClick={() => {
                                if (type.value === 'csv') {
                                  handleExportCSV();
                                } else if (type.value === 'excel') {
                                  handleExportExcel();
                                }
                              }}
                            >
                              <img
                                src={`${process.env.PUBLIC_URL}/SVGIcons/${type.label}.svg`}
                                alt={type.label}
                              />
                              <p>{type.label}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Create Share Link Button */}
                    <button
                      onClick={handleCreateClick}
                      className="alerts-add-button"
                    >
                      + Share Link
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Share Links Table or Mobile Cards */}
            {screenSize > 860 ? (
              <div className="ag-theme-alpine w-full device-view-container">
                <AgGridReact
                  ref={gridRef}
                  rowData={filteredShareLinks}
                  columnDefs={columnDefs}
                  gridOptions={gridOptions}
                  className="device-table-grid zoomOutLevel"
                  popupParent={document.body}
                />
              </div>
            ) : (
              renderMobileCards()
            )}

            {/* External Dropdown */}
            {openDropdownId !== null && dropdownPosition && (
              <div 
                className="fixed w-44 bg-[var(--modal-background)] rounded-[0.625rem] border border-[var(--button-border-color)] z-[9999]"
                style={{
                  top: dropdownPosition?.top,
                  left: dropdownPosition?.left,
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="p-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const selectedLink = filteredShareLinks.find((link: ShareLinkDto) => link.id === openDropdownId);
                      if (selectedLink) {
                        handleEditClick(selectedLink);
                      }
                    }}
                    className="w-full p-2 rounded-sm font-normal text-left text-xs text-[var(--color-sub-title-text)] hover:bg-[var(--button-hover)] flex items-center gap-3"
                  >
                    <Pencil className="w-4 h-4 text-[var(--color-sub-title-text)]" />
                    Edit Details
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const selectedLink = filteredShareLinks.find((link: ShareLinkDto) => link.id === openDropdownId);
                      if (selectedLink) {
                        handleDeleteClick(selectedLink);
                      }
                    }}
                    className="w-full p-2 rounded-sm text-left font-normal text-xs text-[#E67E7D] hover:bg-[var(--button-hover)] flex items-center gap-3"
                  >
                    <Trash2 className="w-4 h-4 text-[#E67E7D]" />
                    Remove Share Link
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && shareLinkToDelete && (
          <ConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={() => {
              if (!isDeleting) {
                setIsDeleteModalOpen(false);
                setShareLinkToDelete(null);
              }
            }}
            onConfirm={handleDeleteConfirm}
            title="Delete Share Link"
            message={`Are you sure you want to delete the share link "${shareLinkToDelete.shareLinkName}"? This action cannot be undone.`}
            confirmText={isDeleting ? 'Deleting...' : 'Delete'}
            cancelText="Cancel"
            type="danger"
            disabled={isDeleting}
          />
        )}

        {/* Status Message */}
        <StatusMessage
          type={statusMessage.type}
          message={statusMessage.message}
          isVisible={statusMessage.isVisible}
          onClose={closeStatusMessage}
          autoHideDelay={4000} // 4 seconds
        />

        {/* Mobile Action Menu */}
        {screenSize <= 860 && renderMobileActionMenu()}
        
        {/* Mobile Export Modal */}
        {screenSize <= 768 && renderMobileExportModal()}
      </div>

      {/* Create Share Link Modal */}
      {isCreateModalOpen && (
        <CreateShareLinkModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateShareLink}
          onNoDevices={() => {
            setStatusMessage({
              type: 'warning',
              message: 'You’ll need to onboard a device before you can create a share link.',
              isVisible: true,
            });
          }}
        />
      )}

      {/* QR Code Modal */}
      {qrModalUrl && (
        <div className="qr-modal-overlay zoomOutLevel" onClick={() => setQrModalUrl(null)}>
          <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="qr-modal-close"
              onClick={() => setQrModalUrl(null)}
              aria-label="Close QR code"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="qr-modal-title">Scan QR Code</h2>
            <p className="qr-modal-subtitle">Scan the QR code for AQI updates</p>
            <div className="qr-modal-body">
              <div className="qr-modal-code-wrapper" ref={qrCanvasWrapperRef}>
                <QRCodeCanvas value={qrModalUrl} size={200} level="H" includeMargin={true} />
              </div>
              <div className='qr-modal-container'>
                <div className="qr-modal-actions">
                  <div className='qr-modal-action-btn-wrapper'>
                    <button className="qr-modal-action-btn" onClick={handleQrCopyLink} title="Copy link">
                      <Copy className="w-5 h-5" />
                    </button>
                    <span>Copy</span>
                  </div>
                  <div className='qr-modal-action-btn-wrapper gri'>
                    <button className="qr-modal-action-btn" onClick={handleQrPrint} title="Print QR">
                      <Printer className="w-5 h-5" />
                    </button>
                    <span>Print</span>
                  </div>
                  <div className='qr-modal-action-btn-wrapper'>
                    <button className="qr-modal-action-btn" onClick={handleQrShare} title="Share link">
                      <Share2 className="w-5 h-5" />
                    </button>
                    <span>Share</span>
                  </div>
                  <div className='qr-modal-action-btn-wrapper'>
                    <button className="qr-modal-action-btn" onClick={handleQrDownload} title="Download QR">
                      <Download className="w-5 h-5" />
                    </button>
                    <span>Download</span>
                  </div>
                </div>

                <div className="qr-modal-footer">
                  <p className="qr-modal-footer-label">Copy This Link To Scan QR Code</p>
                  <div className="qr-modal-link-row">
                    <input
                      type="text"
                      readOnly
                      value={qrModalUrl}
                      className="qr-modal-link-input"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <button
                      type="button"
                      className="qr-modal-link-copy"
                      onClick={handleQrOpenInBrowser}
                      title="Open in browser"
                      >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Share Link Modal */}
      {isEditModalOpen && selectedShareLink && (
        <EditShareLinkModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedShareLink(null);
          }}
          onUpdate={handleEditShareLink}
          shareLinkData={selectedShareLink}
        />
      )}
    </DashboardLayout>
  );
};

export default ShareLinkPage;
